import { useCallback, useContext, useMemo, useState } from 'react';
import { CButton, CCol, CForm, CRow } from '@coreui/react';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FormSetupContext } from '../FormSetup';
import StepCardHeader from '../components/StepCardHeaders';
import Waiting from '../../../../../general/Loadings/Waiting';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import { CInput } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import { API_ADMIN_ACCOUNT_ENRICHMENT_RULES } from '../../../../../../constants';
import { callTokenApi } from '../../../../../../apiCaller';
import { useDispatch } from 'react-redux';
import { setFlexibleModal } from '../../../../../../actions/admin';
import { toast } from 'react-toastify';
import { toastError } from '../../../../../../utils';
import { ConfirmSaveChange } from '../../../../../general/popup';
import isEqual from 'lodash/isEqual';

const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('Name is required').max(200, 'Name cannot be more than 200 characters'),
});

const FormGroup = ({
    name,
    label,
    errors,
    values,
    touched,
    ...props
}) => {
    return (
        <CFormGroup className="mb-3">
            <CLabel htmlFor={name}>{label}</CLabel>
            <CInput
                id={name}
                invalid={touched[name] && !!errors[name]}
                value={values[name]}
                {...props}
            />
            <CInvalidFeedback>
                <ErrorMessage name={name} />
            </CInvalidFeedback>
        </CFormGroup>
    );
}

const NameDescription = ({ setAccountEnrichment }) => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        setEnrichmentList,
        initStepsData,
        accountId,
        isPixelRule,
    } = useContext(FormSetupContext);

    const dispatch = useDispatch();
    const [saveModal, setSaveModal] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const lastEditStep = stepsData.conditions && stepsData.conditions[0].g0.key ? 2 : 1;
    const initialValues = useMemo(() => {
        const base = {
            name: stepsData.name || '',
            description: stepsData.description || '',
        };

        return { ...base };
    }, [stepsData.name, stepsData.description, activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = useCallback(async (values) => {
        const newValues = {
            ...stepsData,
            name: values.name.trim(),
            description: values.description || '',
        };

        if (stepsData.id) {
            if (isEqual(initStepsData, newValues)) {
                dispatch(
                    setFlexibleModal({
                        show: false,
                        component: '',
                        attributes: {}
                    })
                );
                return;
            }

            setStepsData(newValues);
            toggleSaveModal();
        } else {
            setStepsData(newValues);
            toggleSaveModal();
        }
        
    }, [setActiveStep, setStepsData, stepsData, initStepsData]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSaveModal = () => {
        setSaveModal(pre => !pre);
    };

    const onSave = () => {
        const { name, description, conditions, type, ruleType } = stepsData;
        const isCreate = !stepsData.id;

        const data = {
            name,
            description,
            accountId,
            conditions: JSON.stringify({conditions}),
            ruleType,
            ...(isPixelRule && { type }),
            ...(isCreate && ({
                status: true,
            }))
        };

        const pathURL = isCreate ? `${API_ADMIN_ACCOUNT_ENRICHMENT_RULES}/create` : `${API_ADMIN_ACCOUNT_ENRICHMENT_RULES}/${stepsData.id}`;

        setSaveLoading(true);
        callTokenApi(pathURL, isCreate ? 'POST' : 'PUT', data)
            .then((response) => {
                if (response.status === 200 || response.status === 201) {
                    const responseData = response?.data?.data;

                    setEnrichmentList((prev) => {
                        if (isPixelRule) {
                            const existsIndex = prev.findIndex(tpl => tpl.type === responseData.type);

                            if (existsIndex !== -1) {
                                const updatedTemplates = [...prev];
                                updatedTemplates[existsIndex] = responseData;
                                return updatedTemplates;
                            }
                        } else {
                            const existsIndex = prev.findIndex(tpl => tpl.id === responseData.id);

                            if (existsIndex !== -1) {
                                const updatedTemplates = [...prev];
                                updatedTemplates[existsIndex] = responseData;
                                return updatedTemplates;
                            }
                        
                            return [responseData, ...prev];
                        }
                    });

                    setAccountEnrichment((prev) => {
                        let updatedList;
                        if (isPixelRule) {
                            const existsIndex = prev.accountEnrichmentList.findIndex(tpl => tpl.type === responseData.type);

                            if (existsIndex !== -1) {
                                updatedList = [...prev.accountEnrichmentList];
                                updatedList[existsIndex] = responseData;
                            } else {
                                updatedList = [...prev.accountEnrichmentList, responseData];
                            }
                        } else {
                            const existsIndex = prev.accountEnrichmentList.findIndex(tpl => tpl.id === responseData.id);

                            if (existsIndex !== -1) {
                                updatedList = [...prev.accountEnrichmentList];
                                updatedList[existsIndex] = responseData;
                            } else {
                                updatedList = [responseData, ...prev.accountEnrichmentList];
                            }
                        }

                        return {
                            ...prev,
                            accountEnrichmentList: updatedList
                        };
                    });

                    dispatch(
                        setFlexibleModal({
                            show: false,
                            component: '',
                            attributes: {}
                        })
                    );

                    toast.success(`Rules ${stepsData?.id ? 'updated' : 'created'} successfully.`);
                } else {
                    toastError(response);
                }
            })
            .finally(() => {
                setSaveLoading(false);
                toggleSaveModal();
            });
    };

    return (
        <StepCardHeader
            title={`Name & Description`}
            step={3}
            activeStep={activeStep}
            lastEditStep={lastEditStep}
            setActiveStep={() => setActiveStep(3)}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                validateOnBlur={true}
            >
                {({ values, errors, touched, handleChange, isSubmitting, handleSubmit }) => {
                    return (
                        <>
                            <CForm onSubmit={handleSubmit} noValidate>
                                <CRow>
                                    <CCol lg="12">
                                        <FormGroup
                                            name="name"
                                            label="Name"
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            placeholder="Enter a name"
                                            onChange={handleChange}
                                        />
                                    </CCol>

                                    <CCol lg="12">
                                        <FormGroup
                                            name="description"
                                            label="Description"
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            placeholder="Enter a description for your Enrichment Rule"
                                            onChange={handleChange}
                                        />
                                    </CCol>

                                    <CCol lg="12">
                                        <CButton disabled={isSubmitting} type="submit" className="mt-2 px-4" color="primary">
                                            <Waiting isLoading={isSubmitting}>Save & Close</Waiting>
                                        </CButton>
                                    </CCol>
                                </CRow>
                            </CForm>
                        </>
                    );
                }}
            </Formik>
            <ConfirmSaveChange
                show={saveModal}
                onClose={() => setSaveModal(false)}
                isLoading={saveLoading}
                onAccept={onSave}
                title={`Are you sure you want to save?`}
            >
                <p>
                    You are about to save this new rule. After doing this, publish your changes to see your rule working in your data
                    layer.
                </p>
            </ConfirmSaveChange>
        </StepCardHeader>
    )
}

export default NameDescription